import { Paper, Stack, Text, Tooltip } from "@mantine/core";
import type { TimelineEvent } from "../../types/encounter-drg";
import DrgInfo from "./DrgInfo";
import { formatDateTime, getTimelineUserText } from "./helpers";
import {
  DIFF,
  FONT,
  INNER_BOX_VARIANT,
  TIMELINE_CARD,
  TIMELINE_DOT,
  TIMELINE_LINE,
  TIMELINE_SPACING as TS,
  TIMELINE_TITLE_COLOR,
  TW,
} from "./theme";
import type { TimelineVariant } from "./theme";

const TimelineItemRow = ({
  event,
  isLast,
  caseCreatedByEmail,
}: {
  event: TimelineEvent;
  isLast: boolean;
  caseCreatedByEmail?: string | null;
}) => {
  const variant = (event.variant || "neutral") as TimelineVariant;
  const dotStyle = TIMELINE_DOT[variant] || TIMELINE_DOT.neutral;
  const cardTheme = TIMELINE_CARD[variant] || TIMELINE_CARD.neutral;
  const titleColor =
    TIMELINE_TITLE_COLOR[variant] || TIMELINE_TITLE_COLOR.neutral;
  const innerBox = INNER_BOX_VARIANT[variant] || INNER_BOX_VARIANT.neutral;

  // Strip leading prefix like "CDI review finalized: " from message
  const cleanMessage = event.message
    ? event.message
        .replace(
          /^(CDI|CODING)\s+review\s+(unlocked|finalized|completed)\s*:\s*/i,
          "",
        )
        .trim()
    : null;

  const userText = getTimelineUserText(event, caseCreatedByEmail);

  return (
    <div style={{ display: "flex", gap: TS.gap }}>
      {/* Dot + Line column */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: TS.dotSize,
        }}
      >
        <div
          style={{
            width: TS.lineWidth,
            height: TS.spacerHeight,
            background: "transparent",
          }}
        />
        <div style={dotStyle} />
        {!isLast && (
          <div
            style={{
              width: TS.lineWidth,
              flex: 1,
              background: TIMELINE_LINE.color,
            }}
          />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, paddingBottom: TS.itemPaddingBottom }}>
        <Paper
          withBorder
          style={{
            background: cardTheme.bg,
            borderColor: cardTheme.border,
            borderRadius: TS.cardRadius,
            paddingLeft: TS.cardPx,
            paddingRight: TS.cardPx,
            paddingTop: TS.cardPy,
            paddingBottom: TS.cardPy,
          }}
        >
          <Text size={`${FONT.sm}px`} fw={500} c={titleColor} mb={6}>
            {event.title}
          </Text>

          <Text size={`${FONT.xs}px`} c={TW.black}>
            {formatDateTime(event.timestamp)}
            {userText ? ` | ${userText}` : ""}
          </Text>

          {event.versionLabel && (
            <Text size={`${FONT.xs}px`} c={TW.black} mt={TS.smallMarginTop}>
              {event.versionLabel}
            </Text>
          )}

          {event.codesCount > 0 && (
            <Text size={`${FONT.xs}px`} c={TW.black} mt={TS.smallMarginTop}>
              {event.codesCount} confirmed code
              {event.codesCount > 1 ? "s" : ""}
            </Text>
          )}

          {/* Message / Comment box */}
          {cleanMessage && (
            <Paper
              withBorder
              p={TS.innerPadding}
              style={{
                background: innerBox.bg,
                borderColor: innerBox.border,
                borderRadius: TS.innerRadius,
                marginTop: TS.innerMarginTop,
              }}
            >
              <Text size={`${FONT.xs}px`}>
                {event.messageLabel ? (
                  <>
                    <Text span fw={700} c={innerBox.labelColor}>
                      {event.messageLabel}:{" "}
                    </Text>
                    <Text span c={innerBox.textColor}>
                      {cleanMessage}
                    </Text>
                  </>
                ) : (
                  <Text span c={innerBox.textColor}>
                    {cleanMessage}
                  </Text>
                )}
              </Text>
            </Paper>
          )}

          {/* Revision diff */}
          {event.revisionDiff && (
            <Paper
              withBorder
              p={TS.innerPadding}
              style={{
                borderColor: innerBox.border,
                background: "transparent",
                borderRadius: TS.innerRadius,
                marginTop: TS.innerMarginTop,
              }}
            >
              <Stack gap={TS.diffGap}>
                {event.revisionDiff.addedCodes.length > 0 && (
                  <Text size={`${FONT.xs}px`} c={DIFF.addedColor}>
                    <Text span fw={700} c={DIFF.addedColor}>
                      Confirmed:{" "}
                    </Text>
                    {event.revisionDiff.addedCodes.map((item, i) => (
                      <span key={item.code}>
                        {i > 0 && ", "}
                        <Tooltip label={item.description || item.code}>
                          <Text
                            span
                            size={`${FONT.xs}px`}
                            c={DIFF.addedColor}
                            td="underline"
                            style={{
                              textDecorationStyle: "dotted",
                              cursor: "default",
                            }}
                          >
                            {item.code}
                          </Text>
                        </Tooltip>
                      </span>
                    ))}
                  </Text>
                )}
                {event.revisionDiff.removedCodes.length > 0 && (
                  <Text size={`${FONT.xs}px`} c={DIFF.removedColor}>
                    <Text span fw={700} c={DIFF.removedColor}>
                      Removed:{" "}
                    </Text>
                    {event.revisionDiff.removedCodes.map((item, i) => (
                      <span key={item.code}>
                        {i > 0 && ", "}
                        <Tooltip label={item.description || item.code}>
                          <Text
                            span
                            size={`${FONT.xs}px`}
                            c={DIFF.removedColor}
                            td="underline"
                            style={{
                              textDecorationStyle: "dotted",
                              cursor: "default",
                            }}
                          >
                            {item.code}
                          </Text>
                        </Tooltip>
                      </span>
                    ))}
                  </Text>
                )}
                {event.revisionDiff.previousDrg !==
                  event.revisionDiff.currentDrg && (
                  <Text size={`${FONT.xs}px`} c={TW.black}>
                    <Text span fw={700}>
                      DRG:{" "}
                    </Text>
                    {event.revisionDiff.previousDrg || "N/A"} →{" "}
                    {event.revisionDiff.currentDrg || "N/A"}
                  </Text>
                )}
              </Stack>
            </Paper>
          )}

          {/* Working DRG for AI Review Completed */}
          {event.type === "AI_REVIEW_COMPLETED" && event.workingDrg && (
            <DrgInfo
              drg={event.workingDrg}
              label="Working MS-DRG"
              variant={variant}
            />
          )}

          {/* DRG Info */}
          {event.drg && (
            <DrgInfo
              drg={event.drg}
              label={event.reviewType ? `${event.reviewType} MS-DRG` : "MS-DRG"}
              variant={variant}
            />
          )}
        </Paper>
      </div>
    </div>
  );
};

export default TimelineItemRow;
